import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  AdminModal,
  ConfirmDialog,
  FormField,
  LangTabs,
  Toggle,
  LoadingState,
  ErrorState,
  ApiModeBanner,
  inputCls,
} from "../components/AdminUI";
import { useAdminData } from "../../../store/adminDataStore";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import type { HomepageSection, SectionKey } from "../../../store/types";

const SECTION_KEYS: SectionKey[] = ["hero", "philosophy", "cta"];
const NOW = () => new Date().toISOString();

const EMPTY_SECTION: HomepageSection = {
  id: "", section_key: "hero",
  title_zh: "", title_en: "", title_ja: "",
  content_zh: "", content_en: "", content_ja: "",
  image_url: "", is_active: true, sort_order: 99,
};

export default function AdminContentPage() {
  const { state, upsertHomepageSection, removeHomepageSection, status, refetchSections, isApiMode } = useAdminData();
  const t = useAdminT();
  const { showToast } = useToast();

  const [activeSection, setActiveSection] = useState<SectionKey>("hero");
  const [editing, setEditing] = useState<HomepageSection | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [lang, setLang] = useState<"zh" | "en" | "ja">("zh");
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);

  const items = [...state.homepageSections]
    .filter(h => h.section_key === activeSection)
    .sort((a, b) => a.sort_order - b.sort_order);

  const SECTION_META: Record<string, { label: string; icon: string; desc: string }> = {
    hero: {
      label: t("tabifotoAdmin.content.hero_label"),
      icon: "ri-image-line",
      desc: t("tabifotoAdmin.content.hero_desc"),
    },
    philosophy: {
      label: t("tabifotoAdmin.content.philosophy_label"),
      icon: "ri-quill-pen-line",
      desc: t("tabifotoAdmin.content.philosophy_desc"),
    },
    cta: {
      label: t("tabifotoAdmin.content.cta_label"),
      icon: "ri-megaphone-line",
      desc: t("tabifotoAdmin.content.cta_desc"),
    },
  };

  const openAdd = () => {
    setEditing({ ...EMPTY_SECTION, id: `hp-${Date.now()}`, section_key: activeSection });
    setIsNew(true);
  };

  const openEdit = (item: HomepageSection) => { setEditing({ ...item }); setIsNew(false); };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await upsertHomepageSection(editing);
      showToast(t("tabifotoAdmin.common.save_success"), "success");
      setEditing(null);
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item: HomepageSection, v: boolean) => {
    try {
      await upsertHomepageSection({ ...item, is_active: v });
      showToast(t("tabifotoAdmin.common.save_success"), "success");
    } catch {
      showToast(t("tabifotoAdmin.common.save_error"), "error");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await removeHomepageSection(id);
      showToast(t("tabifotoAdmin.common.delete_success"), "success");
    } catch {
      showToast(t("tabifotoAdmin.common.delete_error"), "error");
    }
    setConfirmDel(null);
  };

  const set = <K extends keyof HomepageSection>(k: K, v: HomepageSection[K]) => {
    if (!editing) return;
    setEditing({ ...editing, [k]: v });
  };

  return (
    <AdminLayout
      title={t("tabifotoAdmin.content.title")}
      subtitle={t("tabifotoAdmin.content.subtitle")}
      action={
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#2C2C2C] text-white text-sm hover:bg-[#2C2C2C]/80 cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
          {t("tabifotoAdmin.content.add_section")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      {/* Section selector */}
      <div className="flex gap-3 mb-6">
        {SECTION_KEYS.map(key => {
          const meta = SECTION_META[key] ?? { label: key, icon: "ri-file-line", desc: "" };
          const count = state.homepageSections.filter(h => h.section_key === key).length;
          return (
            <button key={key} onClick={() => setActiveSection(key)}
              className={`flex-1 flex items-start gap-3 p-4 rounded-lg border text-left cursor-pointer transition-all ${activeSection === key ? "border-[#C8B8A6] bg-[#C8B8A6]/5" : "border-[#D9D9D9]/60 bg-white hover:border-[#C8B8A6]/40"}`}>
              <div className={`w-8 h-8 flex items-center justify-center mt-0.5 shrink-0 ${activeSection === key ? "text-[#C8B8A6]" : "text-[#2C2C2C]/30"}`}>
                <i className={`${meta.icon} text-lg`} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-light text-[#2C2C2C]">{meta.label}</p>
                <p className="text-xs text-[#2C2C2C]/40 mt-0.5 leading-relaxed">{meta.desc}</p>
                <p className="text-xs text-[#C8B8A6] mt-1">
                  {t("tabifotoAdmin.content.records_count", { count })}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {status.sections.loading && <LoadingState />}
      {status.sections.error && <ErrorState message={status.sections.error} onRetry={refetchSections} />}

      {!status.sections.loading && !status.sections.error && (
        <div className="space-y-3">
          {items.length === 0 && (
            <div className="bg-white rounded-lg border border-[#D9D9D9]/60 py-12 text-center text-sm text-[#2C2C2C]/30">
              {t("tabifotoAdmin.content.no_items")}
            </div>
          )}
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-lg border border-[#D9D9D9]/60 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-[#2C2C2C]/30 font-mono">sort: {item.sort_order}</span>
                    {!item.is_active && (
                      <span className="text-xs bg-[#D9D9D9]/60 text-[#2C2C2C]/40 px-2 py-0.5 rounded-full">
                        {t("tabifotoAdmin.common.inactive")}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {(["zh", "en", "ja"] as const).map(l => (
                      <div key={l} className="min-w-0">
                        <span className="text-[10px] text-[#2C2C2C]/30 uppercase tracking-wider">{l}</span>
                        <p className="text-sm font-light text-[#2C2C2C] mt-0.5 line-clamp-1">
                          {item[`title_${l}`] || <span className="text-[#2C2C2C]/20 italic">—</span>}
                        </p>
                        <p className="text-xs text-[#2C2C2C]/40 mt-0.5 line-clamp-2">
                          {item[`content_${l}`] || <span className="italic">—</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                  {item.image_url && (
                    <div className="mt-3 w-full h-20 rounded overflow-hidden bg-[#D9D9D9]/20">
                      <img src={item.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Toggle checked={item.is_active} onChange={v => handleToggleActive(item, v)} />
                  <button onClick={() => openEdit(item)} className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] cursor-pointer"><i className="ri-edit-line" /></button>
                  <button onClick={() => setConfirmDel(item.id)} className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/30 hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <AdminModal
          title={isNew ? t("tabifotoAdmin.content.add_section") : t("tabifotoAdmin.content.edit_section")}
          onClose={() => setEditing(null)} onSave={handleSave} saving={saving}
        >
          <div className="grid grid-cols-2 gap-3 mb-3">
            <FormField label={t("tabifotoAdmin.content.section_key")}>
              <select className={inputCls} value={editing.section_key} onChange={e => set("section_key", e.target.value)}>
                {SECTION_KEYS.map(k => <option key={k} value={k}>{SECTION_META[k]?.label ?? k}</option>)}
              </select>
            </FormField>
            <FormField label={t("tabifotoAdmin.common.sort_order")}>
              <input type="number" className={inputCls} value={editing.sort_order} onChange={e => set("sort_order", Number(e.target.value))} />
            </FormField>
          </div>
          <LangTabs activeLang={lang} onChange={setLang} />
          <div className="mt-3">
            <FormField label={`${t("tabifotoAdmin.content.title_field")} (${lang})`}>
              <input className={inputCls}
                value={(editing as Record<string, unknown>)[`title_${lang}`] as string}
                onChange={e => set(`title_${lang}` as keyof HomepageSection, e.target.value as never)} />
            </FormField>
            <FormField label={`${t("tabifotoAdmin.content.content_field")} (${lang})`}>
              <textarea className={`${inputCls} resize-none`} rows={4} maxLength={500}
                value={(editing as Record<string, unknown>)[`content_${lang}`] as string}
                onChange={e => set(`content_${lang}` as keyof HomepageSection, e.target.value as never)} />
            </FormField>
          </div>
          <FormField label={t("tabifotoAdmin.content.image_url")}>
            <input className={inputCls} value={editing.image_url} onChange={e => set("image_url", e.target.value)} placeholder="https://..." />
            {editing.image_url && (
              <div className="mt-2 w-full h-28 rounded overflow-hidden bg-[#D9D9D9]/20">
                <img src={editing.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
          </FormField>
          <label className="flex items-center gap-2 text-sm text-[#2C2C2C]/60 cursor-pointer mt-2">
            <Toggle checked={editing.is_active} onChange={v => set("is_active", v)} />
            {t("tabifotoAdmin.common.active")}
          </label>
        </AdminModal>
      )}

      {confirmDel && (
        <ConfirmDialog
          title={t("tabifotoAdmin.content.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")}
          confirmLabel={t("tabifotoAdmin.common.delete")}
          danger
          onConfirm={() => handleDelete(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </AdminLayout>
  );
}
